import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as moment from 'moment';
import { AuthService } from 'src/app/services/auth.service';
import { SharedService } from '../../../services/shared.service';
import {
  FuelOrdersService,
  OrderPrefill,
  isMobicoPipesAdmin,
  isMobicoPipesRequester,
} from '../../../services/fuel-orders.service';
import { GroupIds } from '../../../enums/groups-ids.enum';

/** Pipa que cae en un día del calendario: sugerida por el motor o ya pedida. */
interface CalendarPipe {
  kind: 'suggested' | 'ordered';
  product: string; // regular | premium | diesel (interno GASPRE)
  label: string; // Extra | Supreme | Diésel (comercial MOBICO)
  liters: number;
  date: string; // YYYY-MM-DD
  status: string | null; // solo pedidas: pending | sent | review | confirmed
  po: string | null;
  suggestion: any; // solo sugeridas: el objeto crudo del back (para el modal)
}

/** Celda del calendario. Un día, todos los productos de la estación. */
interface CalendarDay {
  date: string; // YYYY-MM-DD
  dayNumber: number; // 27
  monthLabel: string | null; // 'ago' — solo en el primer día del mes / de la rejilla
  state: 'past' | 'today' | 'future';
  pipes: CalendarPipe[];
  suggestedCount: number; // punto del carril del Tablero
  orderedCount: number;
  belowMin: boolean; // algún producto sigue debajo del mínimo ese día
  tooltip: string;
}

/** Fila de la rejilla: una semana ISO completa (lunes a domingo). */
interface CalendarWeek {
  caption: string; // 'Semana en curso' | 'Semana del pedido'
  isTarget: boolean;
  days: CalendarDay[];
}

/** Esqueleto de la rejilla (solo fechas): encabezado del carril del Tablero. */
interface HeadWeek {
  caption: string;
  isTarget: boolean;
  days: { date: string; dayNumber: number; state: 'past' | 'today' | 'future' }[];
}

/** Bloque por producto de una estación (tarjeta de "cómo estás hoy"). */
interface SuggestProduct {
  product: string;
  label: string;
  chipClass: string;
  risk: string; // critico | advertencia | cubierta | ok | sin_datos
  riskLabel: string;
  thresholdDays: number;
  /** Días sobre el mínimo con los que se pide: los de la Regla o, si con la pipa que la
   *  estación suele pedir no caben bajo el tope, los que el motor acopló (`ruleCapped`). */
  arrivalDays: number;
  /** La Regla no cabe con su pipa usual y el motor la acopló a ella. */
  ruleCapped: boolean;
  /** Qué regla manda: days (N días de venta), min (el mínimo) o capacity (no cabe bajo el tope).
   *  Las dos NO se suman: se cumple la que pide más litros (MOBICO, 2026-10-09). */
  governs: string | null;
  /** Litros que pide la Regla que manda, y los litros con los que de verdad se pide (menos si se acopló). */
  ruleLevel: number | null;
  orderLevel: number | null;
  /** Pipa que la estación suele pedir (la que más repite en sus entregas de las últimas 2 semanas), o null sin historial. */
  typicalPipe: number | null;
  /** Mínimo operativo en litros: los días de inventario se cuentan sobre él. */
  minOperative: number;
  todayValue: number | null;
  todayBasis: string | null; // real | theoretical
  todayDays: number | null;
  dailySale: number | null;
  tankCapacity: number | null;
  blockedByMissingCapacity: boolean;
  suggestions: any[]; // { date, liters, reason } del back
  activeOrders: any[]; // { date, liters, status, po_number }
  projection: any[]; // cruda del back (para el modal)
  fillPct: number | null; // nivel de hoy sobre la capacidad (barra de la tarjeta)
  rulePct: number | null; // posición de la marca del mínimo en la barra
  reasonText: string; // consecuencia en una línea, en lenguaje de usuario
}

interface SuggestStation {
  id: string;
  name: string;
  products: SuggestProduct[];
  weeks: CalendarWeek[];
  pipesTotal: number;
  litersTotal: number;
  risk: string; // el peor riesgo de sus productos
  riskLabel: string;
  riskRank: number;
  /** Chips "Extra 2 · Diésel 8" de la columna "Qué pedir" del Tablero. */
  demand: { label: string; chipClass: string; pipes: number }[];
  /** Estaciones SIN pipas sugeridas: por qué están tranquilas, en una línea
   *  (la razón del producto que marca el riesgo de la estación). */
  summaryText: string;
  expanded: boolean; // solo Tablero: acordeón de detalle
}

/** Abreviaturas de día en español (0=Dom … 6=Sáb, índice de moment.day()). */
const DAY_ABBR = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/** Encabezado de la rejilla. La semana arranca en lunes (ISO), como el resto
 *  del flujo de pedidos (cronograma y semanas de solicitud). */
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Nombres comerciales MOBICO por producto interno (mismo diccionario del
 *  formulario de solicitud, pipes.component). */
const PRODUCT_LABELS: { [key: string]: string } = {
  regular: 'Extra',
  premium: 'Supreme',
  diesel: 'Diésel',
};

/** Semáforo de riesgo: mismas etiquetas y mismo orden que el grupo "Sugerencia
 *  semanal" de Análisis de Pedido (stock.component), para que las dos vistas
 *  hablen igual. */
const RISK_LABELS: { [key: string]: string } = {
  critico: 'Crítica',
  advertencia: 'Advertencia',
  cubierta: 'Cubierta',
  ok: 'OK',
  sin_datos: 'Sin datos',
};
const RISK_RANK: { [key: string]: number } = {
  critico: 0,
  advertencia: 1,
  cubierta: 2,
  ok: 3,
  sin_datos: 4,
};

const ORDER_STATUS_LABELS: { [key: string]: string } = {
  pending: 'Pendiente',
  sent: 'Enviada',
  review: 'En revisión',
  confirmed: 'Confirmada',
};

/**
 * "Sugerencia de Pedidos" (Etapa 1 de la propuesta Sugerencia de Pedidos de la
 * Semana, 2026-07-23; rediseño 2026-07-27). Componente reutilizable con dos
 * superficies que comparten el calendario y las tarjetas de producto:
 * - Modo ESTACIÓN (default, página /gasprecio/sugerencia-pedidos del menú
 *   solicitante): SOLO la estación en sesión (idStation). Una rejilla de
 *   calendario por estación con las pipas como eventos dentro del día.
 * - Modo TABLERO (boardMode, sección "Sugerencia de Pedidos" del dropdown
 *   Inventario en /gasprecio/general): TODAS las estaciones como una tabla de
 *   una fila por estación, con el carril de días alineado entre filas (el plan
 *   del grupo en el tiempo). El calendario grande solo baja al expandir una
 *   fila, de a una — el auto-expand por estación era la causa del "veo mucha
 *   información" del feedback.
 * Consume el motor semanal GET /fuel-orders/suggest-week (back 2026-07-23).
 * La rejilla cubre el horizonte COMPLETO que devuelve el back (hoy → fin de la
 * semana objetivo, hasta 2 semanas), no solo los 7 días de la semana objetivo:
 * el back proyecta desde hoy+1, así que con "semana siguiente" seleccionada hay
 * pipas sugeridas ANTES del lunes objetivo que antes se anunciaban en el texto
 * y no tenían celda dónde pintarse.
 * "Usar sugerencia" prellena el formulario multi-pipa existente (app-pipes
 * modalOnly, mismo mecanismo que el (+) del Inventario R/T); el usuario solo
 * captura PO y material. Cada chip del calendario prellena SU pipa; el botón de
 * la tarjeta de producto, todas las de ese producto.
 * La capacidad del tanque y la venta diaria de hoy se completan con
 * GET /fuel-orders/week (misma semana), que ya las expone por estación+producto.
 */
@Component({
  selector: 'app-suggest-week',
  templateUrl: './suggest-week.component.html',
  styleUrls: ['./suggest-week.component.scss'],
})
export class SuggestWeekComponent implements OnInit, OnDestroy {
  /** true = sección del Tablero (todas las estaciones del alcance del usuario);
   *  false = página de estación (solo la estación en sesión). */
  @Input() boardMode = false;

  // El 1-click a solicitud es solo del solicitante (el admin en Solicitud de
  // Pipas ve el panel admin, no el formulario de la estación).
  canRequest = isMobicoPipesRequester();
  private readonly isMobicoPipesUser = isMobicoPipesAdmin() || isMobicoPipesRequester();
  /** MOBICO (grupo, admin o solicitante de pipas): los chips de producto toman
   *  los colores del cliente (revisión 06-oct) vía la clase del host; los
   *  demás grupos conservan los del portal. */
  @HostBinding('class.mobico-products')
  get isMobicoFlow(): boolean {
    return this.idGroup === GroupIds.Mobico || this.isMobicoPipesUser;
  }

  readonly weekdays = WEEKDAYS;

  /** Primera carga / cambio de semana o de alcance: la vista todavía no tiene
   *  qué mostrar, así que se pinta el loader. */
  loading = true;
  /** Refresco en caliente (después de enviar una solicitud): la vista se queda
   *  en pantalla y solo se avisa que se está recalculando. */
  refreshing = false;
  errorMsg = '';

  // Modo estación: la estación en sesión.
  idStation = '';
  // Modo tablero: grupo/zona seleccionados (SharedService, como el resto de
  // las vistas del Tablero). El solicitante piloto los ignora (all_stations).
  idGroup = '';
  idZone = '';
  private groupSub$: Subscription | null = null;
  private zoneSub$: Subscription | null = null;
  private inited = false;

  // Semana objetivo: la próxima por default (el pedido se planea con
  // anticipación, acuerdo de la junta 2026-07-22); la actual seleccionable.
  weekMode: 'next' | 'current' = 'next';
  weekStart = '';
  weekEnd = '';
  weekRangeLabel = '';

  stations: SuggestStation[] = [];
  /** Esqueleto de la rejilla, compartido por todas las estaciones (el horizonte
   *  es global): encabezado del carril de días de la tabla del Tablero. */
  headWeeks: HeadWeek[] = [];
  totalPipes = 0;
  totalLiters = 0;
  /** Estaciones con al menos una pipa sugerida. NO se usa summary.stations_at_risk
   *  del back: ese contador también suma las de riesgo 'advertencia', que son
   *  justo las que YA tienen un pedido pendiente cubriendo el mínimo. */
  stationsToOrder = 0;
  /** Estaciones sin pipa sugerida pero con un pedido activo que las cubre. */
  stationsCovered = 0;

  // Modal "¿Por qué esta sugerencia?" (anatomía op-confirm del flujo).
  whyOpen = false;
  whyContext: {
    stationId: string;
    stationName: string;
    product: SuggestProduct;
    suggestion: any;
    dateLabel: string; // "Lun 27 jul"
    dayLabel: string; // "lun 27"
    withoutDays: number | null;
    withValue: number | null;
    withDays: number | null;
    pipesBefore: number;
    orderByLabel: string | null; // "lun 6": fecha límite para pedirla
    urgent: boolean; // la fecha límite es hoy o ya pasó
    sizeBasis: string; // habit | formula | target: de dónde sale el tamaño de la pipa
    habitText: string | null; // "Suele pedir 32,000 lts (la que más repite: 3 de sus 5 entregas de las últimas 2 semanas)"
  } | null = null;

  // Modal de nueva solicitud prellenado (app-pipes modalOnly, patrón del
  // Inventario Real y Teórico: cada apertura crea la instancia desde cero).
  orderModalOpen = false;

  constructor(
    private fuelOrders: FuelOrdersService,
    private sharedService: SharedService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    if (this.boardMode) {
      // Los BehaviorSubject emiten síncrono al suscribirse: los valores
      // actuales quedan puestos ANTES del primer getData (inited evita el
      // refetch duplicado del init).
      this.groupSub$ = this.sharedService.sharedGroup.subscribe((idGroup: any) => {
        if (idGroup && idGroup !== this.idGroup) {
          this.idGroup = idGroup;
          this.idZone = '';
          if (this.inited && !this.loading) {
            this.getData();
          }
        }
      });
      this.zoneSub$ = this.sharedService.sharedZone.subscribe((zone: any) => {
        if (zone !== this.idZone) {
          this.idZone = zone || '';
          if (this.inited && !this.loading) {
            this.getData();
          }
        }
      });
    } else {
      this.idStation = localStorage.getItem('idStation') || this.authService.idStation || '';
    }
    this.inited = true;
    this.getData();
  }

  ngOnDestroy(): void {
    this.groupSub$?.unsubscribe();
    this.zoneSub$?.unsubscribe();
  }

  /** Lunes (ISO) de la semana objetivo según el modo seleccionado. */
  private targetWeekStart(): moment.Moment {
    const start = moment().startOf('isoWeek');
    return this.weekMode === 'next' ? start.add(7, 'days') : start;
  }

  onWeekModeChange(): void {
    this.getData();
  }

  /**
   * @param silent Refresca sin desmontar la vista (solo si ya hay algo pintado).
   *   Se usa al volver de enviar una solicitud: vaciar la pantalla para volver
   *   a llenarla con casi lo mismo se sentía como recargar la página.
   */
  getData(silent = false): void {
    // Sin estación en sesión el modo estación no puede consultar (el servicio
    // omite params vacíos y el back regresaría TODAS las estaciones del usuario).
    if (!this.boardMode && !this.idStation) {
      this.loading = false;
      this.errorMsg = 'No hay una estación en sesión para consultar la sugerencia.';
      return;
    }
    const hot = silent && this.stations.length > 0;
    if (hot) {
      this.refreshing = true;
    } else {
      this.loading = true;
      this.errorMsg = '';
    }
    const week = this.targetWeekStart().format('YYYY-MM-DD');

    // Alcance por superficie: estación en sesión (página del solicitante) ·
    // all_stations (tablero de un solicitante piloto multi-grupo, espejo de
    // las demás vistas de inventario) · grupo/zona seleccionados (tablero).
    let scope: { station?: string; group?: string; zones?: string; all_stations?: string } = {};
    let weekScope: { group?: string; zones?: string } = {};
    if (!this.boardMode) {
      scope = { station: this.idStation };
    } else if (this.canRequest) {
      scope = { all_stations: 'true' };
    } else {
      scope = {
        ...(this.idGroup ? { group: this.idGroup } : {}),
        ...(this.idZone ? { zones: this.idZone } : {}),
      };
      weekScope = scope;
    }

    forkJoin({
      suggest: this.fuelOrders.getSuggestWeek({ week, ...scope }),
      // /week aporta la capacidad del tanque y la venta diaria de hoy por
      // estación+producto; sin grupo devuelve las estaciones del usuario (cubre
      // el modo estación y el all_stations del solicitante).
      week: this.fuelOrders.getWeekOrders({ week, ...weekScope }).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ suggest, week: weekData }) => {
        this.buildViewModel(suggest, weekData);
        this.loading = false;
        this.refreshing = false;
      },
      error: (err) => {
        console.error('Error consultando la sugerencia semanal', err);
        this.refreshing = false;
        // En refresco en caliente la solicitud YA se envió (app-pipes mostró su
        // toast): tumbar la vista a un error sería peor que dejarla como estaba.
        if (!hot) {
          this.errorMsg = 'No se pudo consultar la sugerencia de la semana. Intenta de nuevo.';
          this.loading = false;
        }
      },
    });
  }

  private buildViewModel(suggest: any, weekData: any): void {
    // El acordeón del Tablero sobrevive al refresco: tras enviar una solicitud
    // la estación en la que estabas sigue abierta.
    const openStationId = this.stations.find((st) => st.expanded)?.id || null;

    // week_start viene UTC-anclado a medianoche MX (gotcha de zonas horarias
    // del cronograma): leerlo en UTC da el día correcto. week_end es endOf day
    // MX (23:59) — en UTC brinca al día siguiente, así que se deriva local.
    this.weekStart = moment(suggest?.week_start).utc().format('YYYY-MM-DD');
    this.weekEnd = moment(this.weekStart).add(6, 'days').format('YYYY-MM-DD');
    this.weekRangeLabel = this.rangeLabel(this.weekStart, this.weekEnd);

    // Mapa estación → producto → { tank_capacity, daily_sale } desde /week.
    const capMap = new Map<string, Map<string, any>>();
    for (const st of weekData?.stations || []) {
      const byProduct = new Map<string, any>();
      for (const p of st.products || []) {
        byProduct.set(p.product, p);
      }
      capMap.set(String(st._id), byProduct);
    }

    const todayStr = moment().format('YYYY-MM-DD');
    const calendarDates = this.buildCalendarDates(todayStr);
    this.headWeeks = this.buildHeadWeeks(calendarDates, todayStr);

    this.stations = (suggest?.stations || []).map((st: any) => {
      const stationId = String(st.station_id);
      const products: SuggestProduct[] = (st.products || []).map((p: any) =>
        this.buildProduct(p, capMap.get(stationId))
      );
      const pipesTotal = products.reduce((s, p) => s + p.suggestions.length, 0);
      const litersTotal = products.reduce(
        (s, p) => s + p.suggestions.reduce((acc: number, x: any) => acc + (x.liters || 0), 0),
        0
      );
      const riskRank = products.reduce(
        (worst, p) => Math.min(worst, RISK_RANK[p.risk] ?? 9),
        9
      );
      const worstProduct = products.find((p) => (RISK_RANK[p.risk] ?? 9) === riskRank);
      const risk = worstProduct?.risk || 'ok';
      return {
        id: stationId,
        name: st.alias || '',
        products,
        weeks: this.buildStationWeeks(calendarDates, todayStr, products),
        pipesTotal,
        litersTotal,
        risk,
        riskLabel: RISK_LABELS[risk] || risk,
        riskRank,
        demand: products
          .filter((p) => p.suggestions.length)
          .map((p) => ({ label: p.label, chipClass: p.chipClass, pipes: p.suggestions.length })),
        summaryText: worstProduct
          ? `${worstProduct.label}: ${worstProduct.reasonText}`
          : 'Sin pedido necesario esta semana.',
        expanded: false,
      };
    });

    // Lo que hay que pedir primero: estaciones con pipas sugeridas, luego por
    // gravedad del riesgo y por último alfabético.
    this.stations.sort(
      (a, b) =>
        (b.pipesTotal > 0 ? 1 : 0) - (a.pipesTotal > 0 ? 1 : 0) ||
        a.riskRank - b.riskRank ||
        a.name.localeCompare(b.name, 'es')
    );

    this.totalPipes = this.stations.reduce((s, st) => s + st.pipesTotal, 0);
    this.totalLiters = this.stations.reduce((s, st) => s + st.litersTotal, 0);
    this.stationsToOrder = this.stations.filter((st) => st.pipesTotal > 0).length;
    this.stationsCovered = this.stations.filter(
      (st) => st.pipesTotal === 0 && (st.risk === 'advertencia' || st.risk === 'cubierta')
    ).length;

    if (openStationId) {
      const reopened = this.stations.find((st) => st.id === openStationId);
      if (reopened) {
        reopened.expanded = true;
      }
    }
  }

  /**
   * Fechas de la rejilla: del lunes de la semana en curso (o de la semana
   * objetivo si es anterior) hasta el domingo de la semana objetivo, redondeado
   * a semanas ISO completas. Cubre TODO el horizonte que proyecta el back
   * (hoy+1 … week_end), incluidas las pipas sugeridas antes del lunes objetivo.
   */
  private buildCalendarDates(todayStr: string): string[] {
    const currentWeekStart = moment(todayStr).startOf('isoWeek').format('YYYY-MM-DD');
    const from = currentWeekStart < this.weekStart ? currentWeekStart : this.weekStart;
    const total = moment(this.weekEnd).diff(moment(from), 'days') + 1;
    // Múltiplo de 7 por construcción (ambos extremos son límites de semana ISO);
    // el max evita una rejilla vacía si el back regresara un rango degenerado.
    return Array.from({ length: Math.max(total, 7) }, (_, i) =>
      moment(from).add(i, 'days').format('YYYY-MM-DD')
    );
  }

  private dayState(date: string, todayStr: string): 'past' | 'today' | 'future' {
    if (date < todayStr) {
      return 'past';
    }
    return date === todayStr ? 'today' : 'future';
  }

  /** Etiqueta de mes: solo en el primer día de la rejilla y en cada día 1. */
  private monthLabel(date: string, index: number): string | null {
    const d = moment(date);
    return index === 0 || d.date() === 1 ? MONTHS[d.month()] : null;
  }

  private chunkWeeks<T>(items: T[]): T[][] {
    const weeks: T[][] = [];
    for (let i = 0; i < items.length; i += 7) {
      weeks.push(items.slice(i, i + 7));
    }
    return weeks;
  }

  /** Etiqueta de la fila de semana. Se deriva de la FECHA, no del modo
   *  seleccionado: con "Semana actual" la semana objetivo es la en curso, así
   *  que llamarla "siguiente" sería falso. isTarget solo marca cuál es la
   *  semana del pedido (la que se resalta en verde). */
  private weekCaption(days: string[]): { caption: string; isTarget: boolean } {
    const start = days[0];
    const currentWeekStart = moment().startOf('isoWeek').format('YYYY-MM-DD');
    const nextWeekStart = moment().startOf('isoWeek').add(7, 'days').format('YYYY-MM-DD');

    let caption: string;
    if (start === currentWeekStart) {
      caption = 'Semana en curso';
    } else if (start === nextWeekStart) {
      caption = 'Semana siguiente';
    } else {
      caption = this.rangeLabel(start, moment(start).add(6, 'days').format('YYYY-MM-DD'));
    }
    return { caption, isTarget: start === this.weekStart };
  }

  /** Esqueleto de fechas para el encabezado del carril de la tabla del Tablero. */
  private buildHeadWeeks(dates: string[], todayStr: string): HeadWeek[] {
    return this.chunkWeeks(dates).map((week) => ({
      ...this.weekCaption(week),
      days: week.map((date) => ({
        date,
        dayNumber: moment(date).date(),
        state: this.dayState(date, todayStr),
      })),
    }));
  }

  /** Rejilla de la estación: un día por celda, con las pipas de TODOS sus
   *  productos como eventos dentro del día. */
  private buildStationWeeks(
    dates: string[],
    todayStr: string,
    products: SuggestProduct[]
  ): CalendarWeek[] {
    const days: CalendarDay[] = dates.map((date, index) =>
      this.buildDay(date, index, todayStr, products)
    );
    return this.chunkWeeks(days).map((week) => ({
      ...this.weekCaption(week.map((d) => d.date)),
      days: week,
    }));
  }

  private buildDay(
    date: string,
    index: number,
    todayStr: string,
    products: SuggestProduct[]
  ): CalendarDay {
    const pipes: CalendarPipe[] = [];
    const tooltip: string[] = [`${DAY_ABBR[moment(date).day()]} ${moment(date).date()}`];
    let belowMin = false;

    for (const product of products) {
      for (const s of product.suggestions) {
        if (s.date === date) {
          pipes.push({
            kind: 'suggested',
            product: product.product,
            label: product.label,
            liters: s.liters,
            date,
            status: null,
            po: null,
            suggestion: s,
          });
        }
      }
      for (const o of product.activeOrders) {
        if (o.date === date) {
          pipes.push({
            kind: 'ordered',
            product: product.product,
            label: product.label,
            liters: o.liters,
            date,
            status: o.status || null,
            po: o.po_number || null,
            suggestion: null,
          });
        }
      }

      // Proyección del día: el back ya aplicó las pipas confirmadas, las
      // pendientes y la sugerida, así que una cobertura por debajo del mínimo
      // acá significa que NI CON el pedido sugerido se cubre (típicamente
      // porque falta la capacidad del tanque o no cabe una pipa más grande).
      const proj = product.projection.find((p: any) => p.date === date);
      if (proj) {
        // Alerta solo si ni con las pipas sugeridas la estación llega a los días
        // con los que debe llegar una pipa (la Regla de N días es cuándo PEDIR).
        if (proj.days_coverage != null && proj.days_coverage < product.arrivalDays) {
          belowMin = true;
        }
        tooltip.push(
          `${product.label}: ${this.fmt(proj.inventory)} lts` +
            (proj.days_coverage != null ? ` (${proj.days_coverage.toFixed(1)} días)` : '')
        );
      } else if (date === todayStr && product.todayValue != null) {
        tooltip.push(
          `${product.label}: ${this.fmt(product.todayValue)} lts` +
            (product.todayDays != null ? ` (${product.todayDays.toFixed(1)} días)` : '')
        );
      }
    }

    // Sugeridas primero (son la acción); dentro de cada grupo, por producto.
    pipes.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'suggested' ? -1 : 1));

    return {
      date,
      dayNumber: moment(date).date(),
      monthLabel: this.monthLabel(date, index),
      state: this.dayState(date, todayStr),
      pipes,
      suggestedCount: pipes.filter((x) => x.kind === 'suggested').length,
      orderedCount: pipes.filter((x) => x.kind === 'ordered').length,
      belowMin,
      tooltip: tooltip.join(' · '),
    };
  }

  private buildProduct(p: any, capByProduct: Map<string, any> | undefined): SuggestProduct {
    const weekInfo = capByProduct?.get(p.product);
    const tankCapacity = weekInfo?.tank_capacity ?? p.suggestions?.[0]?.reason?.tank_capacity ?? null;
    const dailySale = weekInfo?.daily_sale ?? p.suggestions?.[0]?.reason?.daily_sale ?? null;
    const todayValue = p.today?.value ?? null;
    const threshold = p.threshold_days;
    // La regla tal como la aplica el motor: los días de inventario se miden
    // SOBRE el mínimo operativo, igual que en Inventarios (Ratio Inv, ¿Cumple?).
    const minOperative = p.rule?.min_operative ?? p.suggestions?.[0]?.reason?.min_operative ?? 0;
    const arrivalDays = p.rule?.arrival_days ?? threshold;
    const orderLevel = p.rule?.order_level ?? (dailySale ? Math.max(minOperative, threshold * dailySale) : null);
    const todayDays =
      todayValue != null && dailySale ? +((todayValue - minOperative) / dailySale).toFixed(1) : null;

    return {
      product: p.product,
      label: PRODUCT_LABELS[p.product] || p.product,
      chipClass: `p-${p.product}`,
      risk: p.risk,
      riskLabel: RISK_LABELS[p.risk] || p.risk,
      thresholdDays: threshold,
      arrivalDays,
      ruleCapped: !!p.rule?.capped_by_pipe,
      governs: p.rule?.governs ?? null,
      ruleLevel: p.rule?.rule_level ?? null,
      orderLevel: p.rule?.order_level ?? null,
      typicalPipe: p.rule?.typical_pipe ?? null,
      minOperative,
      todayValue,
      todayBasis: p.today?.basis ?? null,
      todayDays,
      dailySale,
      tankCapacity,
      blockedByMissingCapacity: !!p.blocked_by_missing_capacity,
      suggestions: p.suggestions || [],
      activeOrders: p.active_orders || [],
      projection: p.projection || [],
      fillPct:
        tankCapacity && todayValue != null ? this.clampPct((todayValue * 100) / tankCapacity) : null,
      rulePct: tankCapacity && orderLevel != null ? this.clampPct((orderLevel * 100) / tankCapacity) : null,
      reasonText: this.reasonText(p, dailySale),
    };
  }

  /** Consecuencia en una línea, en lenguaje de usuario (sin "teórico/Regla").
   *  El inventario de hoy NO se repite acá: ya es el número grande de la
   *  tarjeta de producto. */
  private reasonText(p: any, dailySale: number | null): string {
    if (p.risk === 'sin_datos') {
      return 'Sin datos de inventario para proyectar este producto.';
    }
    if (p.blocked_by_missing_capacity) {
      return 'Falta la capacidad del tanque para poder sugerir un pedido.';
    }
    if (p.suggestions?.length) {
      const first = p.suggestions[0];
      const day = `${DAY_ABBR[moment(first.date).day()]} ${moment(first.date).date()}`;
      const sale = first.reason?.daily_sale ?? dailySale;
      // Días sobre el mínimo sin la pipa (los mide el motor); respaldo para
      // respuestas viejas en caché.
      const before =
        first.reason?.days_before ??
        (first.reason?.inventory_before != null && sale ? first.reason.inventory_before / sale : null);
      const state =
        before == null
          ? 'te quedarías corto'
          : before < 0
          ? 'quedas por debajo del mínimo'
          : `quedas con ${before.toFixed(1)} días sobre el mínimo`;
      const when = first.urgent
        ? 'Hay que pedir hoy'
        : first.order_by
        ? `Pídela a más tardar el ${this.shortDay(first.order_by)}`
        : null;
      return when ? `${when}: sin pipa, el ${day} ${state}.` : `Sin pedir, el ${day} ${state}.`;
    }
    if (p.risk === 'advertencia') {
      const pending = (p.active_orders || []).find((o: any) => o.status !== 'confirmed');
      const when = pending
        ? ` del ${DAY_ABBR[moment(pending.date).day()]} ${moment(pending.date).date()}`
        : '';
      return `Tu pedido pendiente${when} cubre el mínimo (sujeto a confirmación).`;
    }
    if (p.risk === 'cubierta') {
      const confirmed = (p.active_orders || []).find((o: any) => o.status === 'confirmed');
      const when = confirmed
        ? ` del ${DAY_ABBR[moment(confirmed.date).day()]} ${moment(confirmed.date).date()}`
        : '';
      return `Tu pedido confirmado${when} llega antes del mínimo.`;
    }
    return 'Sin pedido necesario esta semana.';
  }

  private rangeLabel(start: string, end: string): string {
    const s = moment(start);
    const e = moment(end);
    if (s.month() === e.month()) {
      return `Semana del ${s.date()} al ${e.date()} de ${MONTHS[e.month()]}`;
    }
    return `Semana del ${s.date()} ${MONTHS[s.month()]} al ${e.date()} ${MONTHS[e.month()]}`;
  }

  private clampPct(value: number): number {
    return Math.max(0, Math.min(100, +value.toFixed(1)));
  }

  private fmt(value: number | null): string {
    if (value == null) {
      return '—';
    }
    return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(value);
  }

  /** "lun 6" para una fecha YYYY-MM-DD. */
  private shortDay(date: string): string {
    const d = moment(date);
    return `${DAY_ABBR[d.day()].toLowerCase()} ${d.date()}`;
  }

  /** Título del chip de una pipa sugerida: litros y fecha límite para pedirla. */
  suggestedTooltip(pipe: CalendarPipe): string {
    const s = pipe.suggestion || {};
    const parts = [`${pipe.label} · ${this.fmt(pipe.liters)} lts sugeridos`];
    if (s.urgent) {
      parts.push('Hay que pedirla hoy');
    } else if (s.order_by) {
      parts.push(`Pídela a más tardar el ${this.shortDay(s.order_by)}`);
    }
    if (s.size_basis === 'habit') {
      parts.push('es lo que más suele pedir (la que más repite en sus entregas de las últimas 2 semanas)');
    }
    if (this.canRequest) {
      parts.push('clic para solicitarla');
    }
    return parts.join(' · ');
  }

  /** Título del chip de una pipa ya pedida: PO + estatus. */
  orderedTooltip(pipe: CalendarPipe): string {
    const parts = [`${pipe.label} · ${this.fmt(pipe.liters)} lts`];
    if (pipe.po) {
      parts.push(`PO ${pipe.po}`);
    }
    if (pipe.status) {
      parts.push(ORDER_STATUS_LABELS[pipe.status] || pipe.status);
    }
    return parts.join(' · ');
  }

  /** Acordeón del Tablero: una estación abierta a la vez. */
  toggleStation(station: SuggestStation): void {
    const willOpen = !station.expanded;
    this.stations.forEach((st) => (st.expanded = false));
    station.expanded = willOpen;
  }

  /** Suma de litros de pipas ya pedidas (confirmadas + pendientes) que caen
   *  ANTES de la fecha de la sugerencia — término del modal "¿Cómo se calculó?". */
  private pipesBefore(product: SuggestProduct, suggestion: any): number {
    return (product.projection || [])
      .filter((d: any) => d.date < suggestion.date)
      .reduce((s: number, d: any) => s + (d.confirmed_liters || 0) + (d.pending_liters || 0), 0);
  }

  openWhy(station: SuggestStation, product: SuggestProduct, suggestion?: any): void {
    const target = suggestion || product.suggestions[0];
    if (!target) {
      return;
    }
    const sale = target.reason?.daily_sale ?? product.dailySale;
    const before = target.reason?.inventory_before ?? null;
    const withValue = target.reason?.inventory_after ?? (before != null ? before + target.liters : null);
    const when = moment(target.date);
    // Días sobre el mínimo: los calcula el motor (misma métrica que Inventarios).
    const withoutDays =
      target.reason?.days_before ?? (before != null && sale ? +(before / sale).toFixed(1) : null);
    const withDays =
      target.reason?.days_after ?? (withValue != null && sale ? +(withValue / sale).toFixed(1) : null);
    // Tamaño: lo que la estación suele pedir (si tiene historial) o el de la fórmula.
    const sizeBasis = target.size_basis || target.reason?.size_basis || 'formula';
    const habitOrders = target.reason?.habit_orders;
    const habitTotal = target.reason?.habit_total;
    const habitText =
      sizeBasis === 'habit' && target.reason?.habit_liters
        ? habitTotal === 1
          ? `Su último pedido fue de ${this.fmt(target.reason.habit_liters)} lts`
          : `Suele pedir ${this.fmt(target.reason.habit_liters)} lts (la que más repite: ${habitOrders} de sus ${habitTotal} entregas de las últimas 2 semanas)`
        : null;
    this.whyContext = {
      stationId: station.id,
      stationName: station.name,
      product,
      suggestion: target,
      dateLabel: `${DAY_ABBR[when.day()]} ${when.date()} ${MONTHS[when.month()]}`,
      dayLabel: `${DAY_ABBR[when.day()].toLowerCase()} ${when.date()}`,
      withoutDays,
      withValue,
      withDays,
      pipesBefore: this.pipesBefore(product, target),
      orderByLabel: target.order_by ? this.shortDay(target.order_by) : null,
      urgent: !!target.urgent,
      sizeBasis,
      habitText,
    };
    this.whyOpen = true;
  }

  closeWhy(): void {
    this.whyOpen = false;
    this.whyContext = null;
  }

  /** Chip del calendario: prellena SOLO esa pipa. */
  usePipe(station: SuggestStation, pipe: CalendarPipe, event?: Event): void {
    event?.stopPropagation();
    if (pipe.kind !== 'suggested') {
      return;
    }
    this.openOrderModal([
      { station: station.id, product: pipe.product, loadDate: pipe.date, capacity: pipe.liters },
    ]);
  }

  /** "Usar sugerencia" de un producto: prellena una fila por pipa sugerida. */
  useProduct(station: SuggestStation, product: SuggestProduct): void {
    const prefills: OrderPrefill[] = product.suggestions.map((s: any) => ({
      station: station.id,
      product: product.product,
      loadDate: s.date,
      capacity: s.liters,
    }));
    this.openOrderModal(prefills);
  }

  /** "Usar todas": todas las pipas sugeridas de la estación (todos los productos). */
  useAllForStation(station: SuggestStation, event?: Event): void {
    event?.stopPropagation();
    const prefills: OrderPrefill[] = [];
    for (const product of station.products) {
      for (const s of product.suggestions) {
        prefills.push({
          station: station.id,
          product: product.product,
          loadDate: s.date,
          capacity: s.liters,
        });
      }
    }
    this.openOrderModal(prefills);
  }

  useFromWhy(): void {
    if (!this.whyContext) {
      return;
    }
    const { stationId, product, suggestion } = this.whyContext;
    const station = this.stations.find((s) => s.id === stationId);
    this.closeWhy();
    if (station) {
      this.openOrderModal([
        {
          station: station.id,
          product: product.product,
          loadDate: suggestion.date,
          capacity: suggestion.liters,
        },
      ]);
    }
  }

  private openOrderModal(prefills: OrderPrefill[]): void {
    if (!this.canRequest || !prefills.length) {
      return;
    }
    this.fuelOrders.nextOrderPrefillList = prefills;
    // Cada apertura crea la instancia app-pipes desde cero (y el cierre la
    // destruye): así el consume del prefill corre en cada click.
    this.orderModalOpen = true;
  }

  /** @param submitted app-pipes avisa si se envió la solicitud o si solo se
   *  cerró el modal. Cancelar no cambia nada, así que no se vuelve a consultar;
   *  enviar sí (las pipas pendientes cuentan en la proyección), pero en
   *  caliente para no vaciar la pantalla. */
  onOrderModalClosed(submitted: boolean): void {
    this.orderModalOpen = false;
    if (submitted) {
      this.getData(true);
    }
  }
}
